import React, { createContext, useContext, useState, useEffect } from 'react';
import {
  GoogleReCaptchaProvider,
  useGoogleReCaptcha,
} from 'react-google-recaptcha-v3';
import envVars from '../env-vars';

type ExecuteRecaptcha = ((action?: string) => Promise<string>) | undefined;

const RecaptchaContext = createContext<ExecuteRecaptcha>(undefined);

export const useRecaptchaExecute = () => useContext(RecaptchaContext);

export const RecaptchaProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [executeRecaptcha, setExecuteRecaptcha] =
    useState<ExecuteRecaptcha>(undefined);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let timeoutId: ReturnType<typeof setTimeout>;
    const activate = () => {
      timeoutId = setTimeout(() => setReady(true), 3000);
    };
    if (document.readyState === 'complete') activate();
    else {
      window.addEventListener('load', activate);
      return () => window.removeEventListener('load', activate);
    }
    return () => clearTimeout(timeoutId);
  }, []);

  return (
    <RecaptchaContext.Provider value={executeRecaptcha}>
      {children}
      {ready && (
        <GoogleReCaptchaProvider
          reCaptchaKey={envVars.REACT_APP_RECAPTCHA_KEY}
          scriptProps={{ async: true, defer: true, appendTo: 'body' }}
        >
          <RecaptchaBridge onReady={setExecuteRecaptcha} />
        </GoogleReCaptchaProvider>
      )}
    </RecaptchaContext.Provider>
  );
};

function RecaptchaBridge({
  onReady,
}: {
  onReady: (fn: ExecuteRecaptcha) => void;
}) {
  const { executeRecaptcha } = useGoogleReCaptcha();

  useEffect(() => {
    if (executeRecaptcha) onReady(executeRecaptcha);
  }, [executeRecaptcha, onReady]);

  return null;
}
